import { Platform } from 'react-native';
import { API_BASE_URL } from '../utils/apiConfig';
import { createTimeoutScope } from './requestTimeout';

export interface ClientErrorReport {
  /** Which screen/boundary caught the crash, e.g. "WorkoutDetail". */
  surface: string;
  message?: string;
  /** React errorInfo.componentStack — names the failing component (key diagnostic). */
  component_stack?: string;
  error_stack?: string;
  workout_id?: string;
  workout_type?: string;
}

/**
 * Best-effort native crash telemetry (away-active-fidelity sprint, 2026-07-22).
 *
 * Posts a caught render crash to `POST /api/native/client-error` so the real
 * `error.message` + `componentStack` are captured server-side (CloudWatch
 * `WORKOUT_DETAIL_CRASH`). Pre-sprint the WorkoutDetail ErrorBoundary only
 * `console.error`'d to the device console, so a data-dependent detail crash
 * couldn't be pinpointed. Fire-and-forget; it NEVER throws — a crashing client
 * must not crash again while reporting.
 */
export async function reportClientError(report: ClientErrorReport): Promise<void> {
  // The reporter itself must be bounded. Before 2026-08-31 this call had no
  // timeout, so on the exact network wedge it exists to describe it would
  // hang forever — a telemetry path that dies of the disease it reports.
  const scope = createTimeoutScope(undefined, TELEMETRY_TIMEOUT_MS);
  try {
    await fetch(`${API_BASE_URL}/api/native/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        surface: report.surface,
        workout_id: report.workout_id,
        workout_type: report.workout_type,
        message: (report.message || '').slice(0, 500),
        component_stack: (report.component_stack || '').slice(0, 1000),
        error_stack: (report.error_stack || '').slice(0, 1000),
        platform: Platform.OS,
      }),
      ...(scope ? { signal: scope.signal } : {}),
    });
  } catch {
    // Best-effort telemetry — swallow all errors.
  } finally {
    scope?.dispose();
  }
}

/**
 * Report that a request exceeded its budget.
 *
 * WHY THIS EXISTS
 * ===============
 * The 2026-08-31 lockout (mattmayer, 8h43m) was only ever diagnosable
 * because the user reported it in person. There is no server-side marker
 * for this failure class and there cannot be one: the failure IS the
 * absence of packets. Timeouts stop the hang; this is the only thing that
 * lets us SEE the next one.
 *
 * WHAT IT CANNOT SEE — read before trusting a quiet dashboard
 * ----------------------------------------------------------
 * In a TOTAL wedge this report cannot send either, for the same reason
 * the original request could not. A silent dashboard therefore does NOT
 * prove absence of the failure. What it does catch:
 *   - partial wedges (some hosts/routes reachable, others hung)
 *   - slow-but-alive networks where the budget is genuinely exceeded
 *   - the first timeout after recovery, once packets flow again
 * The server-side signature of the total case remains what it was in the
 * incident: a long silence followed by a reconnect burst.
 *
 * Rate-limited because a wedged device retries, and a flood of identical
 * reports buys nothing over the first one.
 */
const TELEMETRY_TIMEOUT_MS = 10_000;
const TIMEOUT_REPORT_MIN_INTERVAL_MS = 60_000;
let _lastTimeoutReportAt = 0;

export function reportRequestTimeout(url: string, timeoutMs: number): void {
  const now = Date.now();
  if (now - _lastTimeoutReportAt < TIMEOUT_REPORT_MIN_INTERVAL_MS) return;
  _lastTimeoutReportAt = now;

  // Strip query strings — they can carry user identifiers, and the path is
  // the whole diagnostic value.
  const path = String(url).split('?')[0];

  // Deliberately not awaited. The caller is already on a failure path and
  // must not be delayed further by telemetry.
  void reportClientError({
    surface: 'RequestTimeout',
    message: `Request exceeded ${timeoutMs}ms budget: ${path}`,
  });
}

/** Test seam — reset the rate limiter between cases. */
export function _resetTimeoutReportThrottleForTests(): void {
  _lastTimeoutReportAt = 0;
}
